import { useState } from "react";
import { X, Loader2, User, Wallet } from "lucide-react";
import API from "../utils/api";
import { toast } from "react-hot-toast";

export default function SendTransferModal({ isOpen, onClose, crypto, onSuccess }) {
  const [transferType, setTransferType] = useState("");
  const [recipient, setRecipient] = useState(""); // email for internal, address for external
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

const handleSubmit = async () => {
  // Basic validation
  if (!transferType || !recipient || !amount || !pin || !crypto?.symbol) {
    setError("All fields are required");
    return;
  }

  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount) || numericAmount <= 0) {
    setError("Amount must be greater than 0");
    return;
  }

  setLoading(true);
  setError("");

  // THIS IS THE MOST IMPORTANT PART — LOG EXACTLY WHAT'S SENT
  const payload = {
    recipientEmail: recipient.trim().toLowerCase(),
    cryptoSymbol: crypto.symbol.toUpperCase(), // ← MUST be uppercase like in DB (BTC, ETH)
    amount: numericAmount,
    pin: pin.trim()
  };

  console.log("Sending payload:", payload); // ← CHECK THIS IN BROWSER CONSOLE

  try {
    const { data } = await API.post("/user/transfer/internal", payload);

    toast.success(data.message || "Transfer successful!");
    onSuccess?.(data);
    onClose();
  } catch (err) {
    console.error("Full error:", err.response || err);
    const msg = err.response?.data?.message || "Transfer failed";
    setError(msg);
    toast.error(msg);
  } finally {
    setLoading(false);
  }
};
  if (!isOpen || !crypto) return null;

  const isInternal = transferType === "internal";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-primary">
            Transfer {crypto.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Transfer Type */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Transfer Type</label>
          <select
            value={transferType}
            onChange={(e) => {
              setTransferType(e.target.value);
              setRecipient(""); // clear when switching
            }}
            className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
          >
            <option value="">Select transfer type</option>
            <option value="external">External (On-Chain)</option>
            <option value="internal">Internal (Free & Instant)</option>
          </select>
        </div>

        {/* Recipient Field */}
        {transferType && (
          <div className="mb-5">
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              {isInternal ? "Recipient Email" : "Wallet Address"}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                {isInternal ? (
                  <User className="w-5 h-5 text-primary" />
                ) : (
                  <Wallet className="w-5 h-5 text-primary" />
                )}
              </div>
              <input
                type="text"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={
                  isInternal
                    ? "e.g. friend@swiftvault.com"
                    : "0x123... or bc1q..."
                }
                className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
              />
              {isInternal && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-success bg-success/10 px-2 py-1 rounded">
                  FREE
                </span>
              )}
            </div>
            {isInternal && (
              <p className="text-xs text-gray-500 mt-2">
                Send instantly to any SwiftVault user via email
              </p>
            )}
          </div>
        )}

        {/* Amount */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Amount</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            step="any"
            className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
          />
        </div>

        {/* PIN */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Transaction PIN</label>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter your 6-digit PIN"
            maxLength={6}
            className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!transferType || !recipient || !amount || !pin || loading}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center ${
            !transferType || !recipient || !amount || !pin || loading
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-primary text-white hover:bg-primary/90 shadow-lg"
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-6 h-6 mr-3 animate-spin" />
              Processing...
            </>
          ) : (
            `Send ${amount || ""} ${crypto.symbol}`
          )}
        </button>

        {/* Error */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-xl text-red-700 text-center font-medium">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}